import React, { useState, useEffect } from 'react';
import useAuthStore from '../stores/useAuthStore';
import DashboardLayout from '../components/DashboardLayout';
import { useToast } from '../components/ToastContainer';
import axiosClient from '../services/api';

const StudentProfilePage = () => {
    const { user: authUser, fetchProfile } = useAuthStore();

    const toast = useToast();
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        avatar: null
    });

    const [isUpdating, setIsUpdating] = useState(false);

    useEffect(() => {
        if (authUser) {
            setFormData({
                name: authUser.name || '',
                email: authUser.email || '',
                avatar: authUser.avatar || null
            });
        }
    }, [authUser]);

    const handleAvatarChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setFormData({
                ...formData,
                avatar: file,
                preview: URL.createObjectURL(file),
            });
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsUpdating(true);
        try {
            const payload = new FormData();
            payload.append('name', formData.name);
            payload.append('email', formData.email);
            if (formData.avatar instanceof File) {
                payload.append('avatar', formData.avatar);
            }

            await axiosClient.put('/users/profile', payload, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });

            fetchProfile()
            toast.success('Profile updated successfully!');
        } catch (err) {
            toast.error(err.response?.data?.error || 'Failed to update profile');
        } finally {
            setIsUpdating(false);
        }
    };

    return (
        <DashboardLayout userRole={authUser.role}>
            <div className="min-h-screen bg-neutral-50 dark:bg-neutral-900 py-8">
                <div className="max-w-4xl mx-auto px-4">
                    <div className="mb-8">
                        <h1 className="text-3xl font-bold text-neutral-900 dark:text-white">My Profile</h1>
                        <p className="text-neutral-600 dark:text-neutral-400 mt-2">
                            Update your personal information
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        {/* Profile Form */}
                        <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-sm border border-neutral-200 dark:border-neutral-700 p-6">
                            <h2 className="text-xl font-semibold mb-6 dark:text-neutral-300">Profile Information</h2>
                            <form onSubmit={handleSubmit}>
                                <div className="mb-6 dark:text-neutral-300">
                                    <label className="block text-sm font-medium mb-2">Profile Picture</label>
                                    <div className="flex items-center space-x-4">
                                        <img
                                            src={formData.preview || `${process.env.REACT_APP_SOCKET_URL}/public${formData.avatar}`}
                                            alt="Profile"
                                            className="w-20 h-20 rounded-full object-cover"
                                        />
                                        <label className="px-4 py-2 bg-neutral-200 hover:bg-neutral-300 dark:bg-neutral-700 dark:hover:bg-neutral-600 rounded-lg cursor-pointer">
                                            {!formData.avatar ? 'Upload' : 'Change Photo'}
                                            <input
                                                type="file"
                                                accept="image/*"
                                                onChange={(e) => {
                                                    const file = e.target.files[0];
                                                    if (file) {
                                                        setFormData({ ...formData, avatar: file });
                                                        handleAvatarChange(e);
                                                    }
                                                }}
                                                className="hidden"
                                            />
                                        </label>
                                    </div>
                                </div>

                                <div className="space-y-4 dark:text-neutral-300">
                                    <div>
                                        <label className="block text-sm font-medium mb-2">Full Name</label>
                                        <input
                                            type="text"
                                            value={formData.name}
                                            disabled
                                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                            className="w-full px-4 py-3 border border-neutral-300 dark:border-neutral-600 rounded-lg shadow-sm bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white placeholder-neutral-500 dark:placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
                                            required
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium mb-2">Email</label>
                                        <input
                                            type="email"
                                            value={formData.email}
                                            disabled
                                            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                            className="w-full px-4 py-3 border border-neutral-300 dark:border-neutral-600 rounded-lg shadow-sm bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white placeholder-neutral-500 dark:placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
                                            required
                                        />
                                    </div>
                                    <button
                                        type="submit"
                                        disabled={isUpdating}
                                        className="px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white rounded-lg"
                                    >
                                         {isUpdating ? ('updating..') : ('Save Changes')}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </DashboardLayout>
    );
};

export default StudentProfilePage;